import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ZoomTransformation } from '../../../common/zoom/models/zoom-model';
import { WorldMapCountry, WorldMapShape } from '../../models/worldmap-model';

import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { CommonChartModule } from '../../../common/common-chart.module';

const FACTOR_LINE_HIGHLIGHTED = 4.5;
const FACTOR_LINE_WHITE = 1;
const FACTOR_LINE_SHADOW = 10;

@Component({
  selector: '[chart-shape]',
  standalone: true,
  imports: [CommonModule, CommonChartModule],
  template: `
    <!-- SHADOW
     -->
    <svg:path
      class="country-shape-shadow no-interactive"
      *ngIf="shape.selected"
      [attr.d]="shape.path"
      [attr.stroke-width]="transform ? (transform.k | stroke: STROKE_RANGE_SHADOW) : 1"
    ></svg:path>

    <!-- SHAPE NO HIGHLIGHTED
     -->
    <svg:path
      class="country-shape"
      *ngIf="!shape.selected || !shape.excluded"
      [attr.id]="shape.id"
      [attr.d]="shape.path"
      [attr.fill]="showBackground ? fillColor : COLOR_WHITE"
      [attr.stroke-width]="transform ? (transform.k | stroke: STROKE_RANGE) : 1"
    ></svg:path>

    <!-- HIGHLIGHTED
    -->
    <ng-container *ngIf="shape.selected || shape.excluded">
      <svg:clipPath [attr.id]="'clip-' + shape.id">
        <svg:path [attr.d]="shape.path" [attr.fill]="'black'"></svg:path>
      </svg:clipPath>

      <svg:path
        [attr.clip-path]="'url(#clip-' + shape.id + ')'"
        class="country-shape"
        [attr.id]="shape.id"
        [attr.d]="shape.path"
        [attr.fill]="showBackground ? fillColor : COLOR_WHITE"
        [attr.stroke-width]="transform ? (transform.k | stroke: STROKE_RAGE_HIGHLIGHTED) : 1"
      ></svg:path>
    </ng-container>

    <!-- WHITE LINE SHAPE
    -->
    <svg:path
      class="no-interactive"
      *ngIf="shape.selected || shape.excluded"
      [attr.d]="shape.path"
      fill="none"
      stroke="white"
      [attr.stroke-width]="transform ? (transform.k | stroke: STROKE_RANGE_WHITE) : 1"
    ></svg:path>
  `,
  styleUrls: ['./shape.component.scss']
})
export class ShapeComponent implements OnChanges {
  /**
   * PUBLIC VARIABLES
   */

  public COLOR_WHITE = 'white';
  public fillColor: string = this.COLOR_WHITE;

  // STROKE RANGES
  public STROKE_RANGE = [0.5, 0.1];
  public STROKE_RAGE_HIGHLIGHTED = [this.STROKE_RANGE[0] * FACTOR_LINE_HIGHLIGHTED, this.STROKE_RANGE[1] * FACTOR_LINE_HIGHLIGHTED];
  public STROKE_RANGE_WHITE = [this.STROKE_RANGE[0] * FACTOR_LINE_WHITE, this.STROKE_RANGE[1] * FACTOR_LINE_WHITE];
  public STROKE_RANGE_SHADOW = [this.STROKE_RANGE[0] * FACTOR_LINE_SHADOW, this.STROKE_RANGE[1] * FACTOR_LINE_SHADOW];

  /**
   * Inputs
   */
  @Input()
  shape: WorldMapShape;

  @Input()
  colors: Array<string>;

  @Input()
  complementaryColors: Array<string>;

  @Input()
  dataRange: Array<number>;

  @Input()
  transform: ZoomTransformation;

  @Input()
  public showBackground: boolean;

  @Input()
  private country: WorldMapCountry;

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.country && changes.country.currentValue) || changes.showBackground) {
      this.fillColor = this.getFillColor();
    }
  }

  /**
   * METHODS
   */

  private getColorFromValue(value): string {
    const scaleColor = d3.scaleLinear().domain(this.dataRange).range(this.colors);

    return scaleColor(value);
  }

  private getFillColor() {
    if (!this.dataRange || !this.colors || !this.country || !this.showBackground) {
      return this.COLOR_WHITE;
    }

    return this.getColorFromValue(this.country.scaledCount || this.country.count);
  }
}
